import React, { useEffect, useRef, useState } from 'react'
import { MapboxProvider } from './useMapboxContext'
import {
  Marker,
  Container,
  Controls,
  Zoom,
  ZoomControl,
  Layer,
  PitchControl,
  LocationControl,
  Modal,
} from './components'
import { jsx, useColorMode } from 'theme-ui'
import mapboxgl from 'mapbox-gl/dist/mapbox-gl-csp'
import MapboxWorker from 'worker-loader!mapbox-gl/dist/mapbox-gl-csp-worker'

mapboxgl.workerClass = MapboxWorker
mapboxgl.accessToken = process.env.NEXT_PUBLIC_MAPBOX_API_TOKEN

function Mapbox({
  children,
  locations,
  locationToCentre,
  initialZoom = 16, // Zoom out level when no location is selected
  locationZoom = 15, // Zoom in lvel when a location is selected
  ...props
}) {
  const initialMapCenterLongLat = ['147.32783768176523', '-42.88150606123612']

  const mapContainer = useRef()
  const map = useRef()
  const markerBounds = useRef(new mapboxgl.LngLatBounds())
  const [lng, setLng] = useState(initialMapCenterLongLat[0])
  const [lat, setLat] = useState(initialMapCenterLongLat[1])
  const [zoom, setZoom] = useState(initialZoom)
  const geolocate = useRef(
    new mapboxgl.GeolocateControl({
      positionOptions: {
        enableHighAccuracy: true,
      },
      trackUserLocation: true,
    }),
  )
  const markers = useRef(new Map())
  const previousMarker = useRef()
  const preventMapTransition = useRef()
  const [modal, setModal] = useState(!!locationToCentre)
  const [loaded, setLoaded] = useState(false)

  // Markers
  const [colorMode] = useColorMode()

  useEffect(() => {
    map.current = new mapboxgl.Map({
      container: mapContainer.current,
      center: [lng, lat],
      zoom: zoom,
      attributionControl: false,
      // pitch: 60, // pitch in degrees
      maxPitch: 66,
      /* Presentation style, see 'options.style' section at
       * https://docs.mapbox.com/mapbox-gl-js/api/map/
       */
      style:
        colorMode === 'dark'
          ? 'mapbox://styles/museumofoldandnewart/cko3xsx1j0eel18pvm0veoeqj/draft' ///draft'
          : 'mapbox://styles/museumofoldandnewart/ckpxlf0v60upz17samxzxxpca/draft', ///draft',
    })
    // ? 'mapbox://styles/museumofoldandnewart/ckpmj1o4q5vd218pahumipve0' ///draft'
    map.current.on('styleimagemissing', function(e) {
      console.log('A styleimagemissing event occurred.', e)
    })

    map.current.on('load', () => {
      setLoaded(true)
      // resize on load to fix bug where map does not stretch to 100%
      map.current.resize()
    })

    map.current.on('moveend', function mapMoved() {
      setLng(map.current.getCenter().lng.toFixed(4))
      setLat(map.current.getCenter().lat.toFixed(4))
      setZoom(map.current.getZoom().toFixed(2))
    })
  }, [locations, colorMode])

  return (
    <MapboxProvider
      value={{
        mapContainer,
        map,
        initialZoom,
        locationZoom,
        initialMapCenterLongLat,
        lng,
        setLng,
        lat,
        setLat,
        geolocate,
        zoom,
        setZoom,
        markerBounds,
        markers,
        previousMarker,
        preventMapTransition,
        modal,
        setModal,
        loaded,
      }}
    >
      <Container ref={mapContainer}>{loaded && children}</Container>
    </MapboxProvider>
  )
}

Mapbox.Marker = Marker
Mapbox.Controls = Controls
Mapbox.Zoom = Zoom
Mapbox.ZoomControl = ZoomControl
Mapbox.PitchControl = PitchControl
Mapbox.LocationControl = LocationControl
Mapbox.Layer = Layer
Mapbox.Modal = Modal

export { Mapbox }
